import { AgencyUserDto } from "@api-types/user/AgencyUserDto";
import { AgencyUserFullUpdateDto } from "@api-types/user/AgencyUserFullUpdateDto";

import { EmailChangeDto } from "@api-types/user/EmailChangeDto";
import { EmailChangeRequestParamsDto } from "@api-types/user/EmailChangeRequestParamsDto";
import { OfflineUserDataDto } from "@api-types/user/OfflineUserDataDto";
import { UserLimitSettingsDto } from "@api-types/user/UserLimitSettingsDto";
import { UserNameRecoveryParamsDto } from "@api-types/user/UserNameRecoveryParamsDto";
import { VerifyAndChangePasswordParamsDto } from "@api-types/user/VerifyAndChangePasswordParamsDto";
import { PlayerPromoSendingMode } from "@api-types/user/AgencyUserDto";
import { getAccountActivationToken, getAgencyId, httpClient } from "@utils";
import CaptchaService from "@services/captcha/CaptchaService";
import { ApiRequestContextData, ContextOptions } from "@utils/api-config/types";
import { getApplicationRequestHeaders } from "@utils/api-config/getApplicationRequestHeader";
import { getApiRequestContext } from "@utils/api-config/getApiRequestContext";

export default class AgencyUserApiService {
	static get baseUrl() {
		return `${WEBPACK_BASE_ADDRESS}platform/agency-users`;
	}

	static getRequestHeaders = (
		context: ApiRequestContextData | null
	): HeadersInit => {
		const appHeaders = getApplicationRequestHeaders(context);
		appHeaders["Content-Type" as keyof HeadersInit] = "application/json";
		return appHeaders;
	};
	static async setUserAutomaticLogoutSettings(
		userLimitSettings: UserLimitSettingsDto,
		options?: ContextOptions
	): Promise<void> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl}/${context?.agencyId}/${context?.userId}/limit-settings`,
			JSON.stringify(userLimitSettings),
			this.getRequestHeaders(context)
		);
	}

	static async updateBettingActivityReview(
		options?: ContextOptions
	): Promise<void> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl}/update-last-betting-review-check-date`,
			null,
			this.getRequestHeaders(context)
		);
	}

	// returns new email if successful
	static async activateEmail(
		emailChangeTo: EmailChangeDto,
		options?: ContextOptions
	): Promise<string | void> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl}/email-change`,
			JSON.stringify(emailChangeTo),
			this.getRequestHeaders(context)
		);
	}

	static async checkIsEmailAvailable(
		checkEmail: string,
		options?: ContextOptions
	): Promise<boolean | void> {
		const context = getApiRequestContext(options);

		const body = JSON.stringify(checkEmail);
		return httpClient.post(
			`${this.baseUrl}/email-availability`,
			body,
			this.getRequestHeaders(context)
		);
	}

	// returns true if successful
	static async changeUserEmail(
		emailChange: EmailChangeRequestParamsDto,
		options?: ContextOptions
	): Promise<boolean | void> {
		const context = getApiRequestContext(options);

		const body = JSON.stringify(emailChange);
		return httpClient.post(
			`${this.baseUrl}/email-change`,
			body,
			this.getRequestHeaders(context)
		);
	}

	static async updateUserPin(
		pin: string,
		options?: ContextOptions
	): Promise<void> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl}/update-pin/${context?.username}/${pin}`,
			null,
			this.getRequestHeaders(context)
		);
	}

	static async promoMaterialsApproval(
		isAccepted: PlayerPromoSendingMode,
		options?: ContextOptions
	): Promise<void> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${
				this.baseUrl
			}/promo-material-update/${context?.userId}/${isAccepted}`,
			null,
			this.getRequestHeaders(context),
			{ responseType: "raw" }
		);
	}

	static async changeUserPassword(
		model: VerifyAndChangePasswordParamsDto,
		options?: ContextOptions
	): Promise<boolean> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl}/${context?.agencyId}/${context?.username}/verify-and-change-password`,
			JSON.stringify(model),
			this.getRequestHeaders(context)
		);
	}

	static async updatePersonalData(
		model: AgencyUserFullUpdateDto,
		options?: ContextOptions
	): Promise<void | { errorCode: number; message: string }> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl}/${context?.agencyId}/${context?.userId}/with-password`,
			JSON.stringify(model),
			this.getRequestHeaders(context)
		);
	}

	static async updatePersonalDataWithoutPassword(
		model: AgencyUserFullUpdateDto,
		options?: ContextOptions
	): Promise<void | { errorCode: number; message: string }> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl}/${context?.agencyId}/${context?.userId}`,
			JSON.stringify(model),
			this.getRequestHeaders(context)
		);
	}

	static async recoverUsername(
		model: UserNameRecoveryParamsDto,
		options?: ContextOptions
	): Promise<void> {
		const context = getApiRequestContext(options);

		const token = await CaptchaService.executeCaptcha("usernameRecovery");

		if (token) {
			model.challengeResponse = token;
		}

		return httpClient.post(
			`${this.baseUrl}/recover-username`,
			JSON.stringify(model),
			this.getRequestHeaders(context)
		);
	}

	// this can get with embed - for future reference check AgencyUserDto model
	static async getUserInfo(options?: ContextOptions): Promise<AgencyUserDto> {
		const context = getApiRequestContext(options);

		return httpClient.get(
			`${this.baseUrl}/${context?.agencyId}/${context?.userId}`,
			this.getRequestHeaders(context)
		);
	}

	static async verifyPasswordRecoveryToken(
		passwordToken: string,
		username: string,
		options?: ContextOptions
	): Promise<boolean> {
		const context = getApiRequestContext(options);

		return httpClient.get(
			`${this.baseUrl}/verify-recover-password-token?passwordToken=${passwordToken}&username=${username}`,
			this.getRequestHeaders(context)
		);
	}

	static async getCardUserInfoAsync(
		accountActivationToken: string,
		agencyId: string,
		userName: string,
		options?: ContextOptions
	): Promise<OfflineUserDataDto> {
		const context = getApiRequestContext(options);

		return httpClient.get(
			`${this.baseUrl}/offline-user-data/${agencyId}/${userName}/${accountActivationToken}`,
			this.getRequestHeaders(context)
		);
	}

	static async getForcePasswordChangeCardUserInfoAsync(
		userName: string,
		options?: ContextOptions
	): Promise<OfflineUserDataDto> {
		const context = getApiRequestContext(options);

		return httpClient.get(
			`${
				this.baseUrl
			}/offline-user-data/password/${getAgencyId()}/${userName}/${getAccountActivationToken()}`,
			this.getRequestHeaders(context)
		);
	}

	static async saveNewDefaultStake(
		defaultStake: number,
		options?: ContextOptions
	) {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl}/update-stake-amount`,
			JSON.stringify(defaultStake),
			this.getRequestHeaders(context)
		);
	}

	static async subscribeToNewsletter(
		email: string,
		options?: ContextOptions
	) {
		const parameters = {
			email: email,
			challengeResponse: "",
		};

		const token = await CaptchaService.executeCaptcha("newsletter");

		if (token) {
			parameters.challengeResponse = token;
		}
		const context = getApiRequestContext(options);

		return httpClient.post(
			`${this.baseUrl}/accept-newsletter-anonymous`,
			JSON.stringify(parameters),
			this.getRequestHeaders(context)
		);
	}

	static async updateRealityCheckDuration(
		durationValue: string,
		options?: ContextOptions
	): Promise<void> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl}/update-reality-check`,
			durationValue,
			this.getRequestHeaders(context)
		);
	}

	static async updateChatName(
		chatName: string,
		options?: ContextOptions
	): Promise<void> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl}/update-chat-name/${context?.agencyId}/${context?.userId}`,
			JSON.stringify(chatName),
			this.getRequestHeaders(context)
		);
	}

	static async checkLoginInfo(
		data: {
			userName: string;
			password: string;
		},
		options?: ContextOptions
	): Promise<{
		userAccountLockDurationTypeId: string;
		userAccountLockReasonTypeId: string;
		userAccountLockExplanation: string;
	} | null> {
		const context = getApiRequestContext(options);

		return await httpClient.post(
			`${this.baseUrl}/check-login-info`,
			JSON.stringify(data),
			this.getRequestHeaders(context)
		);
	}

	static async updateLockData(
		data: {
			userName: string;
			password: string;
			userAccountLockDurationTypeId: string;
		},
		options?: ContextOptions
	) {
		const context = getApiRequestContext(options);

		return httpClient.post(
			`${this.baseUrl}/update-lock-data`,
			JSON.stringify(data),
			this.getRequestHeaders(context)
		);
	}
}
